import {useCallback, useContext, useEffect, useState} from 'react';
import {finalize, Subscription} from 'rxjs';

import {MeetingRoomApiService, PersonContext, ProcessedMeetingRoomEvent, useService} from '@shared/core';
import {createGoogleEvent, CreateGoogleEventArgs} from '../../utilities';

export const useMeetingRoomBooking = (meetingRoomId: string, date: string, floorAddress: string) => {
  const [events, setEvents] = useState<ProcessedMeetingRoomEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const person = useContext(PersonContext);
  const meetingRoomApi = useService(MeetingRoomApiService);

  const getMeetingRoomEvents = useCallback((): Subscription => {
    setIsLoading(true);
    return meetingRoomApi
      .getMeetingRoomEvents(date, meetingRoomId)
      .pipe(
        finalize(() => {
          setIsLoading(false);
        }),
      )
      .subscribe((data) => {
        setEvents(data);
      });
  }, [meetingRoomApi, date, meetingRoomId]);

  useEffect(() => {
    const subscription = getMeetingRoomEvents();

    return () => subscription.unsubscribe();
  }, [meetingRoomApi, meetingRoomId, date, getMeetingRoomEvents]);

  const createMeetingRoomEvent = useCallback(
    ({start, end}: Partial<CreateGoogleEventArgs>) => {
      setIsLoading(true);
      const email = person.email;
      const payload = createGoogleEvent({start, end, email, meetingRoomId, floorAddress});

      return meetingRoomApi.createMeetingRoomEvent(payload).pipe(
        finalize(() => {
          setIsLoading(false);
        }),
      );
    },
    [floorAddress, meetingRoomApi, meetingRoomId, person.email],
  );

  return {
    events,
    isLoading,
    createMeetingRoomEvent,
    getMeetingRoomEvents,
  };
};
